/**
 * The product's own list APIs, reachable by collection name.
 *
 * The explorer used to read every collection through its one generic endpoint.
 * That was the safe thing to build first, but it is not what the side menu does:
 * the real screens call their own routers, which join, enrich and compute before
 * they answer. Reading the raw documents instead means a row here can disagree
 * with the same row one screen over.
 *
 * It turns out this does not have to be a trade-off. Every router below already
 * takes an `app_id` — that is what the Project dropdown on those screens is
 * wired to — and the backend intersects it with the caller's own scope. So the
 * explorer can ask the REAL endpoint for a project other than the one the
 * session is in, and still never be shown a project the caller could not open
 * for themselves. The permission check does not move; it stays where it was.
 *
 * What is NOT here matters as much as what is. A collection with no entry falls
 * back to the generic reader: `feedback_master`, `feedback_submission` and the
 * job schedules have list routes that take no `app_id` at all, so asking them
 * for one project would quietly return the session's project instead — the right
 * grid showing the wrong rows, which is worse than an honest generic read.
 * Those stay on the explorer endpoint until their routers grow the parameter.
 */
import { api } from '../../services/api';

export interface NativeList {
  /** The same path the side-menu screen calls. */
  path: string;
  /** Page-size parameter. A couple of the older routers never moved to snake_case. */
  size?: 'page_size' | 'pageSize';
  /** Free-text parameter. Error Master still calls it `q`. */
  search?: 'search' | 'q';
  /** Route takes `sort_by` / `sort_order`. */
  sort?: boolean;
  /** Route takes the column-filter JSON that the grids build. */
  filters?: boolean;
}

/** Shorthand for the shape most routers share. */
const std = (path: string, over: Partial<NativeList> = {}): NativeList =>
  ({ path, size: 'page_size', search: 'search', sort: true, filters: true, ...over });

/** Verified against each router's own signature, not guessed from its name:
 *  every entry here takes `app_id`, and the two odd ones are marked. */
export const NATIVE_LISTS: Partial<Record<string, NativeList>> = {
  agent_master: std('/api/v1/agents/list'),
  ai_model_master: std('/api/v1/ai-models/list', { sort: false }),
  app_config: std('/api/v1/app-configs/list'),
  auth_permissions: std('/api/v1/auth-permissions/list', { sort: false }),
  auth_permission_sets: std('/api/v1/auth-permission-sets/list', { sort: false }),
  auth_role_bindings: std('/api/v1/auth-role-bindings/list', { sort: false }),
  auth_roles: std('/api/v1/auth-roles/list', { sort: false }),
  auth_user_role_assignments: std('/api/v1/auth-user-role-assignments/list', { sort: false }),
  department_master: std('/api/v1/departments/list'),
  designation_master: std('/api/v1/designations/list'),
  district_master: std('/api/v1/districts/list'),
  document_extraction: std('/api/v1/document-extractions/list', { filters: false }),
  document_schema_master: std('/api/v1/document-schemas/list'),
  // The one router that kept both of its original parameter names.
  error_master: std('/api/v1/error-master/list', { size: 'pageSize', search: 'q' }),
  eval_case: std('/api/v1/eval-cases/list'),
  eval_suite: std('/api/v1/eval-suites/list'),
  function_master: std('/api/v1/functions/list'),
  kb_master: std('/api/v1/knowledge-bases/list'),
  log_master: std('/api/v1/log-master/list', { sort: false }),
  ministry_master: std('/api/v1/ministries/list'),
  partner_connections: std('/api/v1/partner-connections/list'),
  partner_master: std('/api/v1/partners/list'),
  personnel_master: std('/api/v1/personnel-master/list', { size: 'pageSize' }),
  pipeline_master: std('/api/v1/pipelines/list'),
  prompt_master: std('/api/v1/prompts/list', { sort: false }),
  rag_master: std('/api/v1/rags/list'),
  unit_master: std('/api/v1/units/list'),
  unit_type_master: std('/api/v1/unit-types/list'),
  unit_villages_master: std('/api/v1/unit-villages/list'),
  user_master: std('/api/v1/users/list'),
  value_sets_master: std('/api/v1/value-sets/list'),
};

/** The explorer's own promise: a field whose NAME reads like a credential is
 *  never rendered. The generic endpoint does this server-side; a native list
 *  answers whatever its screen shows, so the same rule is applied here rather
 *  than being quietly dropped on the way past. */
const SECRET_FIELD =
  /password|mpin|secret|token|ciphertext|nonce|apikey|api_key|credential|privatekey|private_key|salt|signature/i;
const REDACTED = '••• redacted •••';

const redact = (v: unknown): unknown => {
  if (Array.isArray(v)) return v.map(redact);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    Object.entries(v as Record<string, unknown>).forEach(([k, val]) => {
      out[k] = SECRET_FIELD.test(k) ? REDACTED : redact(val);
    });
    return out;
  }
  return v;
};

/** Routers have accumulated three or four envelope shapes between them; a
 *  caller that knew only one would break on the next. */
interface Envelope {
  data?: unknown;
  items?: unknown;
  results?: unknown;
  total?: number;
  pagination?: { totalItems?: number; total?: number };
}

const asRows = (v: unknown): Record<string, unknown>[] | null =>
  (Array.isArray(v) ? (v as Record<string, unknown>[]) : null);

const unwrap = (body: unknown): { items: Record<string, unknown>[]; total: number } => {
  const envelope = (body ?? {}) as Envelope;
  // `data` is itself an envelope on the routers that paginate inside it.
  const inner = (envelope.data ?? {}) as Envelope;
  const items =
    asRows(body)
    ?? asRows(envelope.data)
    ?? asRows(inner.items)
    ?? asRows(envelope.items)
    ?? asRows(envelope.results)
    ?? [];
  const total =
    envelope.pagination?.totalItems ?? envelope.pagination?.total
    ?? envelope.total ?? inner.total ?? items.length;
  return { items: items.map((r) => redact(r) as Record<string, unknown>), total };
};

/** One page from the collection's real list endpoint, scoped to one project. */
export async function fetchNativeRows(spec: NativeList, opts: {
  appId: string;
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: string;
}): Promise<{ items: Record<string, unknown>[]; total: number }> {
  const params: Record<string, unknown> = {
    page: opts.page,
    [spec.size ?? 'page_size']: opts.pageSize,
    app_id: opts.appId,
    // The chart's branch is ONE project, so core/global rows are not part of
    // this answer — the explorer has its own branch for those.
    include_global: false,
    include_deleted: false,
  };
  if (opts.search) params[spec.search ?? 'search'] = opts.search;
  if (spec.sort && opts.sortBy) {
    params.sort_by = opts.sortBy;
    params.sort_order = opts.sortOrder ?? 'asc';
  }
  if (spec.filters && opts.filters) params.filters = opts.filters;

  const res = await api.get(spec.path, { params });
  return unwrap(res.data);
}
